import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PREVIEW_MODE } from "@/lib/previewMode";

const GUEST_NATION_KEY = "vs:guest_nation";
function readGuestNation(): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(GUEST_NATION_KEY); } catch { return null; }
}

/**
 * P0-SHARE-02 — Single source of truth for the user's chosen nation.
 *
 * Reads `profiles.selection_team` (ISO-3 code, e.g. "MEX", "ARG", "BRA").
 * Never falls back to geolocation. Use this — NOT `useGeoCountry()` — every
 * time a share, OG image, viral card or referral needs the user's nation.
 *
 * Caching:
 *  - Per-session, per-userId module cache (avoids refetch on every mount).
 *  - Auto-invalidates when the auth user changes.
 *  - Listens to `vs:nation-changed` window event (dispatched from
 *    NationGate when the user picks/changes their nation).
 */
const CHANGE_EVENT = "vs:nation-changed";
const cache = new Map<string, string | null>();

export interface UseSelectedNationResult {
  /** ISO-3 nation code (e.g. "MEX") or null if not selected. */
  nation: string | null;
  loading: boolean;
}

async function fetchNation(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from("profiles")
    .select("selection_team")
    .eq("id", userId)
    .maybeSingle();
  const value = (data?.selection_team as string | null) ?? null;
  cache.set(userId, value);
  return value;
}

export function useSelectedNation(): UseSelectedNationResult {
  const { user, isLoading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const cached = userId ? cache.get(userId) : null;
  const [nation, setNation] = useState<string | null>(cached ?? null);
  const [loading, setLoading] = useState<boolean>(
    !!userId && !cache.has(userId),
  );

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      // PREVIEW GUEST MODE — surface guest-picked nation for viral cards,
      // OG images and share flows while un-authenticated.
      setNation(PREVIEW_MODE ? readGuestNation() : null);
      setLoading(false);
      const onChangeGuest = () => setNation(PREVIEW_MODE ? readGuestNation() : null);
      if (typeof window !== "undefined") {
        window.addEventListener(CHANGE_EVENT, onChangeGuest);
      }
      return () => {
        if (typeof window !== "undefined") {
          window.removeEventListener(CHANGE_EVENT, onChangeGuest);
        }
      };
    }
    let cancelled = false;
    if (cache.has(userId)) {
      setNation(cache.get(userId) ?? null);
      setLoading(false);
    } else {
      setLoading(true);
      void fetchNation(userId).then((v) => {
        if (!cancelled) {
          setNation(v);
          setLoading(false);
        }
      });
    }
    const onChange = () => {
      cache.delete(userId);
      void fetchNation(userId).then((v) => {
        if (!cancelled) setNation(v);
      });
    };
    if (typeof window !== "undefined") {
      window.addEventListener(CHANGE_EVENT, onChange);
    }
    return () => {
      cancelled = true;
      if (typeof window !== "undefined") {
        window.removeEventListener(CHANGE_EVENT, onChange);
      }
    };
  }, [userId, authLoading]);

  return { nation, loading };
}

/** Fire after `profiles.selection_team` is written so consumers refresh. */
export function notifyNationChanged() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent(CHANGE_EVENT)); } catch { /* noop */ }
}